import React, { useEffect, useMemo, useRef, useState } from 'react';

const HeroSection = () => {
  const [cursorPos, setCursorPos] = useState({ x: 50, y: 50 });
  const [isHovering, setIsHovering] = useState(false);
  const [particles, setParticles] = useState([]);
  const cleanupTimers = useRef([]);

  const glowStyle = useMemo(() => ({
    background: `radial-gradient(circle at ${cursorPos.x}% ${cursorPos.y}%, rgba(253,242,248,0.35), rgba(244,114,182,0.18) 35%, transparent 60%)`,
    opacity: isHovering ? 0.8 : 0,
  }), [cursorPos, isHovering]);

  useEffect(() => () => {
    cleanupTimers.current.forEach(clearTimeout);
    cleanupTimers.current = [];
  }, []);

  const spawnParticle = (x, y) => {
    const id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    const driftX = (Math.random() - 0.5) * 60;
    const driftY = -40 - Math.random() * 30;
    const size = 6 + Math.random() * 10;
    setParticles((prev) => [...prev.slice(-30), { id, x, y, driftX, driftY, size }]);

    const timer = setTimeout(() => {
      setParticles((prev) => prev.filter((particle) => particle.id !== id));
    }, 1400);
    cleanupTimers.current.push(timer);
  };

  const handlePointerMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    const clampedX = Math.min(100, Math.max(0, x));
    const clampedY = Math.min(100, Math.max(0, y));
    setCursorPos({ x: clampedX, y: clampedY });
    spawnParticle(clampedX, clampedY);
  };

  return (
  <section
    id="home"
    data-cursor-hover="cake"
    className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-12 text-gray-900"
    onMouseEnter={() => setIsHovering(true)}
    onMouseLeave={() => setIsHovering(false)}
    onMouseMove={handlePointerMove}
  >
    <div className="absolute inset-0">
      <img
        src="/images/hero-cake.png"
        alt="Elegant celebration cake"
        className="h-full w-full object-cover"
        loading="eager"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-white/65 via-rose-50/35 to-transparent" />
      <div
        className="pointer-events-none absolute inset-0 transition-opacity duration-500"
        style={glowStyle}
      />
      {particles.map(({ id, x, y, driftX, driftY, size }) => (
        <span
          key={id}
          className="hero-particle pointer-events-none absolute"
          style={{
            left: `${x}%`,
            top: `${y}%`,
            width: `${size}px`,
            height: `${size}px`,
            '--drift-x': `${driftX}px`,
            '--drift-y': `${driftY}px`
          }}
          aria-hidden="true"
        />
      ))}
    </div>

    <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12">
      <div className="mx-auto flex max-w-6xl flex-col gap-10 lg:flex-row lg:items-center">
        <div className="w-full lg:max-w-xl text-center lg:text-left">
          <p className="inline-flex items-center gap-2 rounded-full bg-white/70 px-4 py-1 text-sm font-semibold text-pink-600 shadow-sm">
            Crafted with love
            <span className="h-2 w-2 rounded-full bg-pink-500 animate-pulse" />
          </p>
          <h1 className="mt-5 text-4xl sm:text-5xl md:text-6xl font-bold font-serif text-gray-900">
            Nithuja Cakes
          </h1>
          <p className="mt-4 text-xl sm:text-2xl text-gray-700">
            Happiness in Every Slice
          </p>
          <div className="mt-10 flex flex-col sm:flex-row sm:justify-start gap-4">
            <a
              href="#order"
              className="inline-flex items-center justify-center px-8 py-3.5 bg-pink-600 text-white font-semibold rounded-full shadow-lg hover:bg-pink-700 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-pink-600 focus:ring-offset-2"
            >
              Order Your Dream Cake
            </a>
            <a
              href="#gallery"
              className="inline-flex items-center justify-center px-8 py-3.5 border-2 border-pink-200 text-pink-600 font-semibold rounded-full hover:bg-pink-50 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-pink-100 focus:ring-offset-2"
            >
              View Our Gallery
            </a>
          </div>

          <div className="mt-10 flex justify-end lg:hidden -mr-4 sm:-mr-6">
            <img
              src="/images/logo-hand.png"
              alt="Hand presenting Nithuja Cakes card"
              className="w-[min(360px,90vw)] object-contain drop-shadow-2xl animate-float-slow"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </div>

    <div className="pointer-events-none absolute inset-y-0 right-0 hidden lg:block w-[52vw] max-w-[640px]">
      <img
        src="/images/logo-hand.png"
        alt="Hand presenting Nithuja Cakes card"
        className="h-full w-full object-contain object-right animate-float-slow"
        loading="lazy"
      />
    </div>
  </section>
    );
  };

export default HeroSection;
